import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Descriptions } from 'antd';
import dayjs from 'dayjs';
import {
  StyledModal,
  StyledModalHeader,
  MainButton,
} from '../../../../common/form/FormElements.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import { Spinner } from '../../../../common/index.jsx';
import { useGetBugReportQuery } from '../../../../../redux/services/bug_reports.jsx';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import {
  BugReportStateTag,
  bugReportReporterName,
  bugReportResolverName,
  isBugReportResolved,
} from '../bugReportHelpers.jsx';

const formatDateTime = (date) =>
  date ? dayjs(date).format(DATETIME_FORMAT_DATETIME) : '–';

const BugReportDetailModal = ({
  bugReportId,
  onClose,
  onSetResolved,
  canResolve = false,
}) => {
  const { t } = useTranslation();

  const { data: bugReport, isFetching } = useGetBugReportQuery(bugReportId, {
    skip: !bugReportId,
    refetchOnMountOrArgChange: true,
  });

  if (!bugReportId) {
    return null;
  }

  const isResolved = isBugReportResolved(bugReport);

  return (
    <StyledModal
      open={!!bugReportId}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <StyledModalHeader>{t('Nahlásenie chyby')}</StyledModalHeader>

      {isFetching || !bugReport ? (
        <Spinner />
      ) : (
        <>
          <Descriptions
            bordered
            column={1}
            size="small"
            styles={{ label: { width: 180 } }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label={t('Text chyby')}>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {bugReport.text}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('Nahlásil')}>
              {bugReportReporterName(bugReport)}
            </Descriptions.Item>
            <Descriptions.Item label={t('Role')}>
              {bugReport.user_roles || '–'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Dátum nahlásenia')}>
              {formatDateTime(bugReport.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t('URL adresa')}>
              {bugReport.url ? (
                <a href={bugReport.url} target="_blank" rel="noopener noreferrer">
                  {bugReport.url}
                </a>
              ) : (
                '–'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('Stav')}>
              <BugReportStateTag bugReport={bugReport} t={t} />
            </Descriptions.Item>
            <Descriptions.Item label={t('Vyriešená kým')}>
              {isResolved
                ? `${bugReportResolverName(bugReport)}, ${formatDateTime(bugReport.resolved_at)}`
                : '–'}
            </Descriptions.Item>
          </Descriptions>

          {canResolve && (
            <FormButtonsContainer>
              <MainButton
                onClick={() => {
                  onSetResolved?.(bugReport.id, !isResolved);
                  onClose();
                }}
              >
                {isResolved
                  ? t('Označiť ako nevyriešenú')
                  : t('Označiť ako vyriešenú')}
              </MainButton>
            </FormButtonsContainer>
          )}
        </>
      )}
    </StyledModal>
  );
};

BugReportDetailModal.propTypes = {
  bugReportId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSetResolved: PropTypes.func,
  canResolve: PropTypes.bool,
};

export default BugReportDetailModal;
